import { useState } from 'react'
import { formatTimesInText, useTimeFormat } from '../../lib/timeFormat'
import { COVERAGE_STATE_META } from './dailyAssignmentBoard'
import type { CoverageFinding } from './coverageAnalysis'
import { getSubmittedDays, type SubmittedDay } from './scheduleSubmissionQueue'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// A day's tone follows its status category: work stays plain, time off is
// muted, and leave, training, details, and unavailability each carry a tint.
const TONE: Record<string, string> = {
  work: 'border-line bg-canvas',
  off: 'border-line bg-surface-2 text-ink-muted',
  leave: 'border-info/30 bg-info/10',
  training: 'border-success/30 bg-success/10',
  detail: 'border-warning/30 bg-warning/10',
  unavailable: 'border-error/30 bg-error/10',
}

const parts = (iso: string) => iso.split('-').map(Number) as [number, number, number]
const shift = (iso: string, days: number) => {
  const [year, month, day] = parts(iso)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}
const weekdayOf = (iso: string) => {
  const [year, month, day] = parts(iso)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}
const dayNumber = (iso: string) => String(parts(iso)[2])

// The pay period laid out in Sunday to Saturday weeks; days before or after
// the period carry no entry.
function weeksOf(days: SubmittedDay[], periodStart: string, periodEnd: string) {
  const byDate = new Map(days.map((day) => [day.date, day]))
  const weeks: { date: string; day?: SubmittedDay }[][] = []
  let date = shift(periodStart, -weekdayOf(periodStart))
  while (date <= periodEnd) {
    const week = Array.from({ length: 7 }, (_, index) => {
      const current = shift(date, index)
      return { date: current, day: current >= periodStart && current <= periodEnd ? byDate.get(current) ?? { date: current, status: 'Nothing scheduled', category: '', start: null, end: null, assignment: null } : undefined }
    })
    weeks.push(week)
    date = shift(date, 7)
  }
  return weeks
}

const dayLabel = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/**
 * The schedule a pay period submission asks the supervisor to approve, a day
 * at a time, opened from the card so the decision is made on what was
 * submitted.
 */
export default function SubmittedSchedule({ employeeId, employee, periodStart, periodEnd, conflictsOn }: { employeeId: string; employee: string; periodStart: string; periodEnd: string; conflictsOn?: (date: string) => CoverageFinding[] }) {
  const { format } = useTimeFormat()
  const [open, setOpen] = useState(false)
  const [days, setDays] = useState<SubmittedDay[] | null | undefined>(undefined)

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && days === undefined) getSubmittedDays(employeeId, periodStart, periodEnd).then(setDays)
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="flex min-h-11 items-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
      >
        {open ? 'Hide the schedule' : 'View the schedule'}
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto">
          {days === undefined ? (
            <p role="status" className="text-small text-ink-muted">Loading the schedule&hellip;</p>
          ) : days === null ? (
            <p role="alert" className="text-small text-error">The schedule could not be loaded. Try again in a moment.</p>
          ) : days.length === 0 ? (
            <p className="text-small text-ink-muted">No days are scheduled in this pay period.</p>
          ) : (
            <table className="w-full min-w-[42rem] table-fixed border-separate border-spacing-1 text-left text-caption">
              <caption className="sr-only">{`${employee}'s submitted schedule, by week`}</caption>
              <thead>
                <tr>
                  {WEEKDAYS.map((name) => (
                    <th key={name} scope="col" className="px-1 pb-0.5 text-center font-bold text-ink-muted">{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeksOf(days, periodStart, periodEnd).map((week) => (
                  <tr key={week[0].date}>
                    {week.map((cell) =>
                      cell.day ? (
                        <td key={cell.date} className={`h-20 rounded-control border align-top p-1.5 ${TONE[cell.day.category] ?? 'border-line bg-canvas'}`}>
                          <span className="block font-bold">{dayNumber(cell.date)}</span>
                          <span className="block font-semibold">{cell.day.status}</span>
                          {cell.day.start && cell.day.end && (
                            <span className="block whitespace-nowrap">{formatTimesInText(`${cell.day.start}–${cell.day.end}`, format)}</span>
                          )}
                          {cell.day.assignment && <span className="block text-ink-muted">{cell.day.assignment}</span>}
                          {conflictsOn?.(cell.date).map((finding) => (
                            <span key={finding.assignment} className={`mt-0.5 block font-bold ${COVERAGE_STATE_META[finding.state].className}`}>
                              {COVERAGE_STATE_META[finding.state].label}: {finding.assignment} {finding.actual} of {finding.required}
                            </span>
                          ))}
                        </td>
                      ) : (
                        <td key={cell.date} aria-label={`${dayLabel(cell.date)}, outside the pay period`} className="h-20 rounded-control bg-surface-2/40" />
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
